// MOD-F1 (2026-09-28, REPORTERIA-REGLAS §9) — columna y filtro «Campaña» de Moderación, compartidos
// por la bandeja (Administración › Moderación) y por la pestaña de Reportería. El backend solo manda
// el id (`ReportDto.campanaId`): el nombre es multi-idioma y se resuelve aquí contra el catálogo de
// campañas, igual que el combo de campañas de Brillamos Juntos.
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCampaigns, getCampaignName } from '@/api/recognition';
import type { CampaignDto } from '@/api/recognition';

/** Una opción del combo «Campaña». */
export interface OpcionCampana {
  id: string;
  nombre: string;
}

/** Textos de respaldo, ya traducidos (o literales en español para el Excel). */
export interface TextosCampana {
  /** Reconocimiento del día a día: no pertenece a ninguna campaña. */
  sinCampana: string;
  /** La campaña existe en el reporte pero no en el catálogo que se pudo leer (p. ej. fuera de la página). */
  noDisponible: string;
}

/**
 * Nombre visible de la campaña de un caso. Nunca devuelve el id: un id crudo en una celda es el
 * mismo defecto que ya se corrigió con los nombres de persona («Usuario no disponible»).
 */
export function nombreDeCampana(
  campanaId: string | null | undefined,
  campanas: readonly CampaignDto[],
  lang: string,
  textos: TextosCampana,
): string {
  if (!campanaId) return textos.sinCampana;
  const campana = campanas.find((c) => c.id === campanaId);
  return campana ? getCampaignName(campana, lang) : textos.noDisponible;
}

/**
 * Opciones del combo a partir de los ids de campaña QUE TIENEN reportes (MOD-F1: «solo campañas que
 * tengan reportes asociados»). Sin repetidos, sin el día a día (null) y ordenadas por nombre.
 */
export function opcionesDeCampana(
  campanaIds: readonly (string | null | undefined)[],
  nombreDe: (id: string) => string,
): OpcionCampana[] {
  const ids = [...new Set(campanaIds.filter((id): id is string => Boolean(id)))];
  return ids
    .map((id) => ({ id, nombre: nombreDe(id) }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
}

/** Misma página que ya pide el combo de campañas de Reportería (`RecognitionAnalyticsView`). */
const PAGINA_DE_CAMPANAS = 100;

/**
 * Catálogo de campañas + resolución de nombres en el idioma de quien mira (`nombreDe`) y en español
 * literal para el Excel (`nombreEnEspanol`), mismo criterio que el resto de los libros de Reportería.
 */
export function useNombresDeCampana(): {
  nombreDe: (campanaId: string | null | undefined) => string;
  nombreEnEspanol: (campanaId: string | null | undefined) => string;
} {
  const { t, i18n } = useTranslation('recognition');
  const campanasQuery = useCampaigns(undefined, 1, PAGINA_DE_CAMPANAS);
  const campanas = useMemo(() => campanasQuery.data?.items ?? [], [campanasQuery.data]);
  const textos: TextosCampana = {
    sinCampana: t('reports.moderation.campaignNone'),
    noDisponible: t('reports.moderation.campaignUnavailable'),
  };
  return {
    nombreDe: (id) => nombreDeCampana(id, campanas, i18n.language, textos),
    nombreEnEspanol: (id) => nombreDeCampana(id, campanas, 'es', TEXTOS_CAMPANA_EXCEL),
  };
}

/** Texto LITERAL en español del Excel (el archivo no cambia con el idioma de quien lo exporta). */
export const TEXTOS_CAMPANA_EXCEL: TextosCampana = {
  sinCampana: 'Sin campaña',
  noDisponible: 'Campaña no disponible',
};
